import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isCardioSession, type Session } from "@heraion/shared";

import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { useRhythmKindLabels } from "../Progress/useRhythmKindLabels";
import { useLiveAnnouncement } from "../primitives/useLiveAnnouncement";
import { RHYTHM_KINDS, type RhythmKind } from "../Progress/progressMath";
import type { UiTheme } from "../theme";

// isCardioSession reads only `source`; #419's countsAsStrength also wants setCount > 0,
// which a list row (no exerciseResults) cannot say.
/** #321: the list of sessions of one kind. A picked session stays whatever
 *  its kind, so the selection never loses a row from under the athlete. */
export function filterByKind(
  items: Session[] | null,
  kind: RhythmKind,
  keep: readonly string[],
): Session[] | null {
  if (items === null || kind === "all") return items;
  const want = kind === "cardio";
  return items.filter((s) => isCardioSession(s) === want || keep.includes(s.id));
}

/** Strength | Cardio | All over the list: the chips and the words of the
 *  Rhythm card, the same per-account choice. After a tap, a screen reader
 *  hears how many sessions the loaded pages hold of that kind. */
export function DiaryKindFilter({
  theme,
  kind,
  count,
  onKind,
}: {
  theme: UiTheme;
  kind: RhythmKind;
  /** The sessions shown; null while the list loads. */
  count: number | null;
  onKind: (k: RhythmKind) => void;
}) {
  const { t } = useTranslation();
  const labels = useRhythmKindLabels();
  const { speak, region } = useLiveAnnouncement();
  const tapped = useRef(false);
  useEffect(() => {
    if (!tapped.current || count === null) return;
    tapped.current = false;
    speak(
      count === 0
        ? t("mobile.workouts.kindEmpty", { kind: labels[kind], defaultValue: "{{kind}}: no sessions in the loaded pages" })
        : t("mobile.workouts.kindAnnounce", { kind: labels[kind], count, defaultValue: "{{kind}}: {{count}} sessions loaded" }),
    );
  }, [kind, count, labels, speak, t]);
  return (
    <>
      <ChipChoiceRow
        theme={theme}
        choices={RHYTHM_KINDS}
        selected={kind}
        onSelect={(k) => {
          tapped.current = k !== kind;
          onKind(k);
        }}
        label={(k) => labels[k]}
        chipA11yLabel={(k) => labels[k]}
        groupA11yLabel={t("mobile.workouts.kindFilter", { defaultValue: "Show" })}
      />
      {region}
    </>
  );
}
